import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useTypeSelector } from '../../store';
import { createFaq, updateFaq } from '../../actions/faq';
import { selectFaqList } from '../../selectors/faq';
import { Button } from '../Button';
import Switch from '../sw';
import { TypesButton } from '../../constants/ButtonTypes';
import { typography } from '../../helpers/styles/typography';
import { units } from '../../helpers/styles/units';
import type { FaqItem, FaqParentProps, FaqStatus } from './types';
import { MAX_ACTIVE_FAQ_COUNT } from './types';
import { FaqAnswerEditor, isFaqAnswerEmpty } from './FaqAnswerEditor';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${units(12)};
  max-width: ${units(520)};
  margin-top: ${units(16)};
`;

const StyledInput = styled.input`
  width: 100%;
  min-height: ${units(32)};
  border: 1px solid #d7d7d7;
  padding: ${units(8)};
`;

const StyledButtons = styled.div`
  display: flex;
  gap: ${units(8)};
`;

const StyledSwitchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(4)};
`;

const StyledSwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${units(8)};
`;

const StyledSwitchLabel = styled.span`
  ${typography(10)};
`;

const StyledLimitHint = styled.span`
  ${typography(10)};
  color: #c0392b;
`;

type Props = FaqParentProps & {
    initialData?: FaqItem | null;
    onCancel: () => void;
    onSaved: () => void;
};

const countActiveFaq = (
    list: FaqItem[],
    parent: FaqParentProps,
    excludeId?: number,
) => list.filter(item => {
    if (item.status !== 'ACTIVE' || item.id === excludeId) {
        return false;
    }
    if (parent.categoryId) {
        return item.categoryId === parent.categoryId;
    }
    if (parent.oftenSearchedId) {
        return item.oftenSearchedId === parent.oftenSearchedId;
    }
    return true;
}).length;

export const FaqForm = ({
    initialData = null,
    categoryId,
    oftenSearchedId,
    onCancel,
    onSaved,
}: Props) => {
    const dispatch = useAppDispatch();
    const list = useTypeSelector(selectFaqList);
    const faqItems = useMemo((): FaqItem[] => {
        if (Array.isArray(list)) {
            return list;
        }
        return list?.data ?? [];
    }, [list]);
    const parentProps = useMemo<FaqParentProps>(
        () => (categoryId ? { categoryId } : { oftenSearchedId }),
        [categoryId, oftenSearchedId],
    );
    const [question, setQuestion] = useState(initialData?.question || '');
    const [answer, setAnswer] = useState(initialData?.answer || '');
    const [order, setOrder] = useState(String(initialData?.order ?? 1));
    const [status, setStatus] = useState<FaqStatus>(
        initialData?.status === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE',
    );
    const [isSaving, setIsSaving] = useState(false);

    const activeCount = useMemo(
        () => countActiveFaq(faqItems, parentProps, initialData?.id),
        [faqItems, parentProps, initialData?.id],
    );

    const canActivate = activeCount < MAX_ACTIVE_FAQ_COUNT;
    const isActivationBlocked = !canActivate && status !== 'ACTIVE';

    const handleOrderChange = (value: string) => {
        setOrder(value.replace(/\D/g, ''));
    };

    const handleToggle = (checked: boolean) => {
        if (checked && !canActivate) {
            return;
        }
        setStatus(checked ? 'ACTIVE' : 'INACTIVE');
    };

    const handleSubmit = async () => {
        if (status === 'ACTIVE' && !canActivate) {
            return;
        }

        if (isFaqAnswerEmpty(answer)) {
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                question,
                answer,
                order: Number(order) || 1,
                status,
                ...(categoryId ? { categoryId } : {}),
                ...(oftenSearchedId ? { oftenSearchedId } : {}),
            };

            if (initialData?.id) {
                await dispatch(updateFaq({ id: initialData.id, ...payload })).unwrap();
            } else {
                await dispatch(createFaq(payload)).unwrap();
            }

            onSaved();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <StyledForm onSubmit={e => e.preventDefault()}>
            <StyledInput
                placeholder="Вопрос"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                required
                title="Вопрос"
            />
            <FaqAnswerEditor
                value={answer}
                onChange={setAnswer}
                placeholder="Ответ"
            />
            <StyledInput
                placeholder="Порядок"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={order}
                onChange={e => handleOrderChange(e.target.value)}
                title="Порядок"
            />
            <StyledSwitchWrapper>
                <StyledSwitchRow>
                    <StyledSwitchLabel>Статус</StyledSwitchLabel>
                    <Switch
                        name="faq-status"
                        value={status === 'ACTIVE'}
                        onChange={handleToggle}
                        type="checkbox"
                        title
                        disabled={isActivationBlocked}
                    />
                </StyledSwitchRow>
                {isActivationBlocked && (
                    <StyledLimitHint>
                        {`Достигнут лимит: не более ${MAX_ACTIVE_FAQ_COUNT} активных вопросов`}
                    </StyledLimitHint>
                )}
            </StyledSwitchWrapper>

            <StyledButtons>
                <Button typeButton={TypesButton.PRIMARY} onClick={handleSubmit} disabled={isSaving}>
                    Сохранить
                </Button>
                <Button typeButton={TypesButton.LIGHT} onClick={onCancel}>
                    Отмена
                </Button>
            </StyledButtons>
        </StyledForm>
    );
};
